// Seuil « Incomplète » (spec anti-perte §5.2.3) : l'audio mesuré est inférieur à la
// durée d'horloge moins max(60 s, 2 %). Une horloge absente ou absurde (réglage de
// l'heure pendant la prise) ne déclenche JAMAIS l'état : mieux vaut rater une
// alerte que d'accuser à tort une rencontre complète.
import { TAKE_COPY } from "./take-copy";
import { formatTrashDuration } from "./trash";

export const INCOMPLETE_MIN_GAP_MS = 60_000;
export const INCOMPLETE_GAP_RATIO = 0.02;
export const MAX_PLAUSIBLE_ELAPSED_MS = 86_400_000;

export function isIncomplete(measuredMs: number, elapsedMs: number | null | undefined): boolean {
  if (elapsedMs == null || !Number.isFinite(elapsedMs)) return false;
  if (elapsedMs <= 0 || elapsedMs > MAX_PLAUSIBLE_ELAPSED_MS) return false;
  return measuredMs < elapsedMs - Math.max(INCOMPLETE_MIN_GAP_MS, INCOMPLETE_GAP_RATIO * elapsedMs);
}

// Écart « mineur » : moins d'un morceau de 30 s (une coupure de batterie perd au
// plus le morceau en cours). Ton informatif, ni pastille ni confirmation avant
// l'analyse — retenu par Thierry le 2026-09-30 (SIG-260929-160530 : un bandeau
// d'alerte pour 10 s sur 2 h 17 min).
export const INCOMPLETE_MINOR_GAP_MS = 30_000;
// … et moins de 10 % de l'enregistrement : une idée de 59 s amputée de 29 s
// n'est pas un écart mineur (revue du 30-09).
export const INCOMPLETE_MINOR_GAP_RATIO = 0.1;
// Moins de 10 % gardés : « presque tout » manque, quel que soit l'écart (une
// idée de 20 s dont il reste 1 s n'est pas un écart mineur).
export const MOSTLY_LOST_RATIO = 0.1;

export type GapKind = "minor" | "major" | "mostlyLost";

export function gapKind(measuredMs: number, totalMs: number): GapKind {
  if (measuredMs < MOSTLY_LOST_RATIO * totalMs) return "mostlyLost";
  const ecartMs = totalMs - measuredMs;
  return ecartMs < INCOMPLETE_MINOR_GAP_MS && ecartMs < INCOMPLETE_MINOR_GAP_RATIO * totalMs
    ? "minor"
    : "major";
}

export function isMinorGap(measuredMs: number, totalMs: number): boolean {
  return gapKind(measuredMs, totalMs) === "minor";
}

// Dit l'écart, jamais deux totaux à soustraire. « environ » : l'écart est une
// estimation (taille d'un fichier illisible, ou horloge) — à la seconde sous la
// minute, à la minute au-delà (jamais « environ 1 min 28 s »). Jamais
// « 0 seconde », jamais deux fois la même durée.
export function incompleteMessage(measuredMs: number, totalMs: number): string {
  const total = formatTrashDuration(totalMs);
  const presqueTout = () =>
    TAKE_COPY.incompleteMostlyLost(
      total,
      measuredMs >= 1_000 ? formatTrashDuration(measuredMs) : null,
    );
  const kind = gapKind(measuredMs, totalMs);
  if (kind === "mostlyLost") return presqueTout();
  const ecartMs = Math.max(1_000, totalMs - measuredMs);
  if (kind === "minor") return TAKE_COPY.incompleteMinor(formatTrashDuration(ecartMs), total);
  const arrondiMs = ecartMs < 60_000 ? ecartMs : Math.round(ecartMs / 60_000) * 60_000;
  const ecart = formatTrashDuration(arrondiMs);
  // L'arrondi atteint le total (29 s gardées sur 2 min) : « presque tout ».
  if (arrondiMs >= totalMs || ecart === total) return presqueTout();
  return TAKE_COPY.incomplete(ecart, total);
}
